import { useState, useCallback, useContext } from 'react';
import { Lead, LeadService, PaginatedLeadResponse } from './lead.service';
import { AuthContext } from '../../../context/authcontext';

interface PaginationData {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  hasMore: boolean;
  pageSize: number;
}

interface UseLeadsReturn {
  leads: Lead[];
  loading: boolean;
  backgroundLoading: boolean;
  error: string | null;
  pagination: PaginationData;
  fetchLeads: (page?: number, pageSize?: number, filters?: Record<string, any>) => Promise<void>;
  refreshLeads: () => Promise<void>;
  addLead: (lead: Lead) => void;
  updateLead: (lead: Lead) => void;
  removeLead: (leadId: number) => void;
  lastFetched: Date | null;
  isInitialized: boolean;
  clearBackgroundLoading: () => void;
  clearLeadsData: () => void;
}

export const useLeads = (): UseLeadsReturn => {
  const { selectedTenantId } = useContext(AuthContext);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(false);
  const [backgroundLoading, setBackgroundLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetched, setLastFetched] = useState<Date | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [pagination, setPagination] = useState<PaginationData>({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    hasMore: false,
    pageSize: 10
  });

  // Cache to store fetched pages and prevent duplicate requests
  const [fetchedPages, setFetchedPages] = useState<Set<number>>(new Set());
  const [pageData, setPageData] = useState<Map<number, Lead[]>>(new Map());

  const clearBackgroundLoading = useCallback(() => {
    setBackgroundLoading(false);
  }, []);

  const fetchLeads = useCallback(async (page: number = 1, pageSize: number = 10, filters?: Record<string, any>) => {
    try {
      setLoading(true);
      setError(null);
      
      const response: PaginatedLeadResponse = await LeadService.getLeadsByPage(page, pageSize, filters);
      
      // Update pagination data
      setPagination({
        currentPage: page,
        totalPages: response.totalPages,
        totalCount: response.totalCount,
        hasMore: response.hasMore,
        pageSize: response.pageSize
      });

      // Cache the page data
      setPageData(prev => new Map(prev).set(page, response.leads));
      setFetchedPages(prev => new Set(prev).add(page));

      // If this is the first page or we're refreshing, replace leads
      // Otherwise, append to existing leads (for infinite scroll scenarios)
      if (page === 1) {
        setLeads(response.leads);
      } else {
        // For pagination, typically you'd replace with current page data
        // For infinite scroll, you'd append
        setLeads(response.leads);
      }

      setLastFetched(new Date());
      setIsInitialized(true);
      
      // Set background loading after main API completes
      setBackgroundLoading(true);
    } catch (err) {
      console.error('Error fetching leads:', err);
      setError('Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshLeads = useCallback(async () => {
    // Clear cache and fetch first page
    setFetchedPages(new Set());
    setPageData(new Map());
    setIsInitialized(false);
    await fetchLeads(1, 10);
  }, [fetchLeads]);

  // Clear all data when tenant changes
  const clearLeadsData = useCallback(() => {
    setLeads([]);
    setError(null);
    setIsInitialized(false);
    setFetchedPages(new Set());
    setPageData(new Map());
    setPagination({
      currentPage: 1,
      totalPages: 1,
      totalCount: 0,
      hasMore: false,
      pageSize: 10
    });
  }, []);

  const addLead = useCallback((lead: Lead) => {
    setLeads(prev => [lead, ...prev]);
    // Update pagination count
    setPagination(prev => ({
      ...prev,
      totalCount: prev.totalCount + 1
    }));
    // Clear cache since data has changed
    setFetchedPages(new Set());
    setPageData(new Map());
  }, []);

  const updateLead = useCallback((updatedLead: Lead) => {
    setLeads(prev => 
      prev.map(lead => 
        lead.lead_id === updatedLead.lead_id ? updatedLead : lead
      )
    );
    // Update cache as well
    setPageData(prev => {
      const newPageData = new Map(prev);
      newPageData.forEach((leads, pageNum) => {
        const updatedLeads = leads.map(lead =>
          lead.lead_id === updatedLead.lead_id ? updatedLead : lead
        );
        newPageData.set(pageNum, updatedLeads);
      });
      return newPageData;
    });
  }, []);

  const removeLead = useCallback((leadId: number) => {
    setLeads(prev => prev.filter(lead => lead.lead_id !== leadId));
    // Update pagination count
    setPagination(prev => ({
      ...prev,
      totalCount: Math.max(0, prev.totalCount - 1)
    }));
    // Clear cache since data has changed
    setFetchedPages(new Set());
    setPageData(new Map());
  }, []);

  return {
    leads,
    loading,
    backgroundLoading,
    error,
    pagination,
    fetchLeads,
    refreshLeads,
    addLead,
    updateLead,
    removeLead,
    lastFetched,
    isInitialized,
    clearBackgroundLoading,
    clearLeadsData
  };
}; 